import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { DataMap } from '@grapecity/wijmo.grid'

import {
    BaseComponent,
    DailySafetyCoordinationMeeting,
    FeatureType,
    Grid,
    ListItem,
    ModalSize,
    VpJob,
    VpUser,
} from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { DailySafetyCoordinationMeetingComponent } from './daily-safety-coordination-meeting.component'

@Component({
    selector: 'app-daily-safety-coordination-meetings',
    templateUrl: './daily-safety-coordination-meetings.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
    ],
})
export class DailySafetyCoordinationMeetingsComponent extends BaseComponent {
    public grid: Grid<DailySafetyCoordinationMeeting>

    jobMap!: DataMap
    employeeMap!: DataMap
    activeJobList: ListItem<string>[] = []
    activeEmployeeList: ListItem<number>[] = []
    allJobs: VpJob[] = []
    allEmployees: VpUser[] = []

    constructor() {
        super()
        this.grid = new Grid<DailySafetyCoordinationMeeting>(this.services)
            .setupModalEditing(
                this.services.data.dailySafetyCoordinationMeetings,
                'Daily Safety Coordination Meeting',
                DailySafetyCoordinationMeetingComponent,
                () => DailySafetyCoordinationMeeting.createNew(),
                this,
                ModalSize.extraLarge,
            )
            .wrapColumnHeaders()
            .filterBy(item => [
                item.job,
                item.jobDisplay,
                item.jobPhase,
                item.segue,
                item.yesterdayInjuryWho,
                item.foremanSignature,
                item.acknowledgedWhy,
            ])
        this.grid.canEdit = this.canEdit
        this.grid.canAdd = this.canEdit
        this.grid.canDelete = this.canEdit
        this.refresh()
    }

    public refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.vpJobs.queryAllByAction('GetAllJobs'),
                    this.services.data.get<VpUser[]>('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', true)),
                    this.services.data.get<VpUser[]>('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', false)),
                    this.services.data.dailySafetyCoordinationMeetings.queryAll(),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()

                        const allJobs = result[0] as VpJob[]
                        this.allJobs = allJobs
                        this.activeJobList = allJobs
                            .filter(job => job.jobStatus === 1)
                            .sort((a, b) => a.id.trim().localeCompare(b.id.trim(), undefined, { numeric: true }))
                            .map(job => new ListItem(job.id, `${job.id.trim()} ${job.name}`))
                        this.jobMap = new DataMap(
                            allJobs.map(j => ({ id: j.id, display: `${j.id.trim()} ${j.name}` })),
                            'id',
                            'display',
                        )

                        const activeUsers = result[1] as VpUser[]
                        this.allEmployees = result[2] as VpUser[]
                        this.activeEmployeeList = activeUsers
                            .slice()
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(u => new ListItem(u.id, u.name))
                        this.employeeMap = new DataMap(this.allEmployees, 'id', 'name')

                        const items = result[3] as DailySafetyCoordinationMeeting[]
                        for (const item of items) {
                            item.jobDisplay = this.jobDisplayForItem(item.job)
                        }
                        this.grid.items = items
                    },
                }),
        )
    }

    jobDisplayForItem(jobId: string | null | undefined): string {
        if (!jobId) return ''
        const job = this.allJobs.find(j => j.id === jobId || j.id.trim() === jobId.trim())
        return job ? `${job.id.trim()} ${job.name}` : jobId
    }

    public get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.SafetyChecklistEdit])
    }
}
